import { Box, Group, Paper, Stack, Text, Tooltip, Title } from '@mantine/core'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useMediaQuery } from '@mantine/hooks'
import { DrivesByStateByVehicle } from '@/utils/scheduling'
import { generateTimeSlots, getDrivePosition } from './schedulingTimelineHelpers'
import { DetailedDriveStatistics, RouteResponse } from '@/api/main/types.gen'
import { isEmpty } from 'lodash-es'
import { secondsToHHM, formatTime, getTimezone } from '@/utils/datetime'
import { useNavigate } from '@tanstack/react-router'
import { memo, useMemo } from 'react'
import DriveTypeBadge from './DriveBadge'
import { getRouteDisplay } from '@/utils/routes'

dayjs.extend(utc)
dayjs.extend(timezone)

function TimeGrid({ timeSlots }: { timeSlots: string[] }) {
  // Calculate position based on actual number of slots (use length - 1 for intervals)
  const numIntervals = timeSlots.length - 1
  return (
    <>
      {timeSlots.map((timeSlot, index) => (
        <Box
          key={timeSlot + index}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: `${(index / numIntervals) * 100}%`,
            height: '1px',
            backgroundColor: 'var(--mantine-color-gray-3)',
            zIndex: 1,
          }}
        />
      ))}
    </>
  )
}

function TimeLabels({ timeSlots, isMobile }: { timeSlots: string[]; isMobile: boolean }) {
  // Calculate position based on actual number of slots (use length - 1 for intervals)
  const numIntervals = timeSlots.length - 1
  return (
    <>
      {timeSlots.map((timeSlot, index) => (
        <Box
          key={timeSlot + index}
          style={{
            position: 'absolute',
            left: isMobile ? '-50px' : '-70px',
            top: `${(index / numIntervals) * 100}%`,
            transform: 'translateY(-50%)',
            fontSize: isMobile ? '12px' : '14px',
            color: 'light-dark(var(--mantine-color-gray-7), #c9c9c9)',
            fontWeight: 600,
            zIndex: 2,
            minWidth: isMobile ? '40px' : '50px',
            textAlign: 'right',
          }}
        >
          {timeSlot}
        </Box>
      ))}
    </>
  )
}

const formatIntervalBlock = (dateString: string) => formatTime(dateString, getTimezone())

const IntervalBlock = memo(function IntervalBlock({
  drive,
  position,
  isMobile,
  routes,
}: {
  drive: DetailedDriveStatistics
  position: { top: string; height: string }
  isMobile: boolean
  routes?: RouteResponse[]
}) {
  const routeDisplay = getRouteDisplay(drive.route_id, routes)
  const label = `${routeDisplay}, ${formatIntervalBlock(drive.drive_started_at!)} - ${formatIntervalBlock(drive.drive_finished_at!)}`

  const navigate = useNavigate()

  const handleClick = () => navigate({ to: '/drive-details/$id', params: { id: drive.drive_id.toString() } })

  const isCancelled = drive.state === 'cancelled_in_progress'
  const backgroundColor = isCancelled ? 'var(--mantine-color-red-1)' : 'var(--mantine-color-blue-1)'
  const borderColor = isCancelled ? 'var(--mantine-color-red-6)' : 'var(--mantine-color-blue-6)'
  const driveTextColor = 'light-dark(var(--mantine-color-dark-8), var(--mantine-color-dark-9))'
  return (
    <Tooltip label={label + (isCancelled ? ' (Cancelled)' : '')} withArrow position="top">
      <Paper
        withBorder
        shadow="sm"
        style={{
          position: 'absolute',
          left: '3%',
          right: '3%',
          top: position.top,
          height: position.height,
          backgroundColor: backgroundColor,
          borderLeft: `4px solid ${borderColor}`,
          zIndex: 1,
          overflow: 'hidden',
          minHeight: isMobile ? '35px' : '45px',
          cursor: 'pointer',
          padding: '4px',
        }}
        onClick={handleClick}
      >
        <Stack gap={1} style={{ height: '100%' }}>
          <Box style={{ position: 'absolute', top: 'calc(50% - 2px)', right: 5, transform: 'translateY(-50%)' }}>
            <DriveTypeBadge driveType={drive.drive_type} />
          </Box>
          <Text size="xs" fw={600} p="unset" c={driveTextColor}>
            {routeDisplay}
          </Text>
          <Text size="xs" c={driveTextColor}>
            {secondsToHHM(drive.duration_seconds)}
          </Text>
          <Group justify="space-between" mt="auto">
            <Text size="xs" fw={500} c={driveTextColor} style={{ fontSize: '11px' }}>
              {formatIntervalBlock(drive.drive_started_at!)} - {formatIntervalBlock(drive.drive_finished_at!)}
            </Text>
          </Group>
        </Stack>
      </Paper>
    </Tooltip>
  )
})

export const PastDrivesTimeline = memo(function PastDrivesTimeline({
  drives,
  vehicles,
  timelineStart,
  routes,
}: {
  drives: DrivesByStateByVehicle
  vehicles: Array<{ id: string }>
  timelineStart: dayjs.Dayjs
  routes?: RouteResponse[]
}) {
  const isMobile = useMediaQuery('(max-width: 768px)')

  const allPastDrives = useMemo(
    () =>
      Object.values(drives)
        .filter(Boolean)
        .flatMap(v => v.finished),
    [drives]
  )

  const timeSlots = useMemo(() => generateTimeSlots(), [])
  const getPosition = useMemo(() => getDrivePosition(timelineStart), [timelineStart])

  if (isEmpty(allPastDrives)) {
    return null
  }

  return (
    <Box
      mb="xl"
      style={{
        gridColumnStart: 1,
        gridColumnEnd: vehicles.length + 1,
      }}
    >
      <Title order={3} mt="md" mb="sm" c="light-dark(#868e96, #c9c9c9)">
        {timelineStart.format('DD.MM')} - {timelineStart.add(1, 'day').format('DD.MM')}
      </Title>

      <Paper shadow="sm" p="unset" radius="md" withBorder pt="var(--mantine-spacing-xl)">
        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${vehicles.length}, 1fr)`,
          }}
        >
          {vehicles.map((vehicle, index) => {
            const vehicleId = vehicle.id
            const pastDrives = drives[vehicleId]?.finished || []
            const isLast = index === vehicles.length - 1

            return (
              <Box
                key={vehicleId}
                style={{
                  borderRight: isLast ? 'none' : '1px solid var(--mantine-color-gray-3)',
                  boxSizing: 'border-box',
                }}
              >
                <Box
                  style={{
                    position: 'relative',
                    height: isMobile ? '450px' : '1080px',
                    marginLeft: isMobile ? '50px' : '70px',
                    marginBottom: '40px',
                  }}
                >
                  <TimeGrid timeSlots={timeSlots} />
                  <TimeLabels timeSlots={timeSlots} isMobile={isMobile} />

                  {pastDrives.map(drive => (
                    <IntervalBlock
                      key={drive.drive_id}
                      drive={drive}
                      position={getPosition(drive)}
                      isMobile={isMobile}
                      routes={routes}
                    />
                  ))}
                </Box>
              </Box>
            )
          })}
        </Box>
      </Paper>
    </Box>
  )
})
